import { classSelection, switchClassSelection } from '../host/class-selection';
import { missionSelection, switchMissionSelection } from '../host/mission-selection';
import { appDestination } from './app-destination';
import { readLocal, writeLocal } from '@ndrc/app-sdk/client';
import { ActionMenu } from '@ndrc/ui';
import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import type { JSX } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ActivityBoard } from '../activity/ActivityBoard';
import { createClock } from '../lib/clock';
import type { MeResult } from '../lib/queries';
import { useDeskApps, useStudentSpace, useStudentWorkspace, useWorkspace } from '../lib/queries';
import { stopObservation, useObservation } from '../host/observation';
import { usePresenceHeartbeat } from '../lib/usePresence';
import { INSTALLED_APPS, findManifest } from '../host/registry';
import { useBodyFullscreen } from '../window/useBodyFullscreen';
import { useBrowserFullscreen } from '../window/useBrowserFullscreen';
import { arrangeTiles } from '../window/tiling';
import { t } from '../messages';
import { Dock } from './Dock';
import { MenuBar } from './MenuBar';
import { FullscreenHint } from './FullscreenHint';
import { WindowSlot } from './WindowSlot';
import { DeskProvider } from './context';
import type { DeskContextValue, TeacherSection, WindowFrame } from './context';
import {
  restoreStack,
  closeWindow,
  focusOrOpen,
  minimizeWindow,
  nextFocusPath,
  orderedWindows,
  windowKeyOf,
} from './stack';
import type { DeskWindow, WindowStack } from './stack';

/**
 * Le bureau : le fond d'écran, la barre de menu, le dock, et **une pile de fenêtres**.
 *
 * Depuis le 7 septembre 2026, plusieurs fenêtres sont ouvertes en même temps — l'enseignant a
 * renversé la décision d'accélération qui n'en autorisait qu'une : « ce que je comprends pas c'est
 * pourquoi je peux pas avoir plusieurs applications qui s'ouvrent en même temps ». Le modèle de la
 * pile, et les raisons de sa forme, sont dans `desk/stack.ts` ; ici vit le câblage.
 *
 * **Deux vérités, et une seule direction entre elles.**
 *
 * 1. L'**adresse** dit quelle fenêtre est au premier plan. Ouvrir, cliquer une fenêtre du fond,
 *    cliquer une icône du dock, revenir en arrière dans l'historique : tout passe par une
 *    navigation, donc tout passe par le même chemin. Il n'existe aucun `focusedKey` en état React
 *    qui pourrait diverger de la barre d'adresse.
 * 2. L'**état** (`stack`) dit ce que l'adresse ne peut pas porter : les *autres* fenêtres
 *    ouvertes, leur ordre d'empilement, leur état réduit, et la sous-route que chacune retrouvera
 *    en repassant devant.
 *
 * L'adresse alimente l'état (la synchronisation ci-dessous, et elle seule) ; l'état n'alimente
 * jamais l'adresse en dehors d'un geste explicite (fermer, réduire, ouvrir). C'est ce qui empêche
 * la boucle, et c'est pourquoi les fonctions de `stack.ts` rendent le **même objet** quand rien ne
 * change.
 *
 * Le plein écran est posé **ici**, jamais dans `AppWindow` : le bouton de la barre de menu doit
 * fonctionner même quand aucune fenêtre n'est montée (le bureau nu). Il a deux moitiés, et elles
 * sont dans cet ordre : `useBrowserFullscreen` demande au navigateur le vrai plein écran (barre
 * d'adresse et onglets compris — c'est le geste de projection en classe) et **dit** ensuite s'il
 * l'a obtenu ; `useBodyFullscreen` ne fait que poser `data-fs` sur le `body` d'après cette
 * réponse, ce qui escamote la barre de menu et le dock. Le bureau ne décide donc jamais seul qu'il
 * est en plein écran : il le lit sur le document. C'est ce qui fait que la touche Échap du
 * navigateur remet tout en place, sans qu'aucune touche ne soit écoutée ici.
 *
 * Le plein écran ne concerne que la fenêtre **active** : les autres sont masquées le temps de la
 * projection (`desk.css`, `body[data-fs] .window--behind`) et reviennent exactement où elles
 * étaient — même position, même contenu — quand on en sort.
 */

/** Une seule horloge pour tout le bureau — et donc un seul `new Date()`, dans `lib/clock.ts`. */
const clock = createClock();

export function Desk({ me }: { me: MeResult }): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const route = `${pathname}${location.search}${location.hash}`;

  const deskRef = useRef<HTMLDivElement>(null);
  const storageKey = `desk:${me.id}`;
  const [stack, setStack] = useState(() =>
    focusOrOpen(restoreStack(readLocal(storageKey, null)), route),
  );
  useEffect(() => {
    writeLocal(storageKey, stack);
  }, [stack, storageKey]);
  const [syncedPath, setSyncedPath] = useState(route);

  // L'unique passage « adresse → état », et il se fait **pendant le rendu**, pas dans un effet.
  // C'est le patron que React prescrit pour ajuster un état quand une entrée change : React relance
  // aussitôt le composant sans rien peindre entre-temps, là où un effet aurait fait clignoter une
  // fenêtre absente à chaque changement d'adresse (et `react-hooks/set-state-in-effect` le refuse).
  // La condition ne peut pas boucler : `syncedPath` vaut `pathname` dès le second passage, et
  // `focusOrOpen` rend le même objet quand la fenêtre visée est déjà devant, à la bonne sous-route
  // et non réduite.
  if (syncedPath !== route) {
    setSyncedPath(route);
    setStack(focusOrOpen(stack, route));
  }

  const focusKey = windowKeyOf(pathname);
  const windows = useMemo(() => orderedWindows(stack), [stack]);
  const frontAppId = useMemo(
    () => windows.find((w) => w.key === focusKey)?.appId ?? null,
    [focusKey, windows],
  );

  // Défaut VISIBLE, jamais un choix silencieux : le cœur refuse de deviner la classe d'un
  // enseignant multi-classe (`desk.apps` lève plutôt que de choisir), la barre de menu affiche
  // donc un sélecteur dès qu'il y en a plusieurs, pré-réglé sur la première.
  const classId = classSelection() ?? me.classes[0]?.id;
  const [classSelectionError, setClassSelectionError] = useState(false);
  const className = useMemo(
    () => (classId ? me.classes.find((k) => k.id === classId)?.name : me.classes[0]?.name),
    [classId, me.classes],
  );

  // « Je suis là, dans cette application, sur cet écran. » Le SEUL appelant de
  // `presence.heartbeat` du bureau — sans lui, le suivi de séance de l'enseignant
  // (`teacher.presence`) ne peut afficher que « jamais connecté », pour tout le monde, pour
  // toujours. L'app rapportée est celle du **premier plan** : c'est celle sur laquelle l'élève
  // travaille, pas celle qui traîne derrière.
  usePresenceHeartbeat({
    classId: classId ?? me.classes[0]?.id,
    appId: frontAppId,
    route: pathname,
  });

  /**
   * **Quand l'enseignant lit le travail d'un élève, tout le bureau bascule sur l'espace de cet
   * élève** — le dock, les fenêtres déjà ouvertes, les outils qu'il ouvrira ensuite.
   *
   * Deux lectures, une seule servie : la sienne (`desk.resolveWorkspace`) hors observation,
   * celle de l'élève (`teacher.studentWorkspace`) pendant. Les deux ont la même forme, donc
   * `AppHost` n'a pas deux chemins de montage — et comme `workspace.workspaceId` change, sa clé
   * change, donc un outil DÉJÀ ouvert suit l'espace sans que l'enseignant ait à le refermer.
   *
   * `desk.apps` suit la classe de l'élève : l'enseignant voit exactement le dock que son élève a
   * sous les yeux, cadenas compris — jamais un outil que la classe n'a pas.
   */
  const observing = useObservation();
  const deskApps = useDeskApps(observing?.classId ?? classId);
  const observedSpace = useStudentSpace(observing?.classId, observing?.userId);
  const selectedMission = missionSelection();
  const [selectionError, setSelectionError] = useState(false);
  const ownWorkspace = useWorkspace(classId, selectedMission?.runId);
  const observedWorkspace = useStudentWorkspace(observing);
  const workspace = observing === null ? ownWorkspace : observedWorkspace;

  const visibleAppIds =
    observing === null ? deskApps.data?.appIds : observedSpace.data?.enabledAppIds;
  const enabledAppIds = useMemo(() => new Set(visibleAppIds ?? []), [visibleAppIds]);
  const runningAppIds = useMemo(
    () => new Set(windows.flatMap((w) => (w.appId === null ? [] : [w.appId]))),
    [windows],
  );

  const fs = useBrowserFullscreen();
  useBodyFullscreen(fs.active);

  /** Le geste commun à « ouvrir » et « ramener devant » : une navigation, rien d'autre. Une
   * fenêtre déjà ouverte est rappelée à SA sous-route (`/app/crm/contacts`, pas `/app/crm`) et
   * remplace l'entrée d'historique courante — changer de fenêtre trente fois ne doit pas remplir
   * l'historique de trente entrées. Une fenêtre neuve, elle, en empile une : « précédent » referme
   * ce qu'on vient d'ouvrir, ce que tout le monde attend. */
  const open = useCallback(
    (key: string, freshPath: string) => {
      const known = stack.windows.find((w) => w.key === key);
      if (known === undefined) {
        void navigate(freshPath);
        return;
      }
      void navigate(known.path, { replace: true });
    },
    [navigate, stack.windows],
  );

  const openApp = useCallback(
    (appId: string, explicitRoute?: string) => {
      if (explicitRoute !== undefined) {
        const destination = appDestination(appId, explicitRoute);
        if (destination && findManifest(appId)) void navigate(destination);
        return;
      }
      const route = findManifest(appId)?.ui.student.defaultRoute ?? '';
      open(`app:${appId}`, `/app/${appId}${route.startsWith('/') ? route : ''}`);
    },
    [open, navigate],
  );
  const openProfile = useCallback(() => {
    open('me', '/me');
  }, [open]);
  /** Réservé aux enseignants côté dock — mais ce n'est pas ce bouton qui protège quoi que ce soit :
   * chaque opération de cet espace refuse un élève côté serveur (`role: 'teacher'`). Voir le
   * commentaire de tête de `teacher/TeacherSpace.tsx`. */
  const openTeacher = useCallback(() => {
    open('teacher', '/teacher/presence');
  }, [open]);
  /** L'espace enseignant ouvert **sur une section visée** — le geste que proposent les écrans
   * d'élève quand c'est l'enseignant qui les lit (« Distribuer un fichier à cette classe »).
   *
   * Il ne passe pas par `open` : celui-ci rappelle une fenêtre déjà ouverte à l'adresse qu'elle
   * avait mémorisée, ce qui ferait atterrir « Distribuer un fichier » sur le suivi de séance quand
   * l'espace enseignant traîne déjà derrière. La navigation, elle, ouvre ou ramène la même fenêtre
   * unique (`focusOrOpen`, clé `teacher`) à la section demandée — c'est le seul cas où l'on veut
   * imposer la sous-route plutôt que la retrouver. */
  const openTeacherSection = useCallback(
    (section: TeacherSection) => {
      void navigate(`/teacher/${section}`);
    },
    [navigate],
  );
  /** La consigne du jour, dans une fenêtre. Ouverte par le dock, comme « Mon compte » — elle reste
   * ainsi atteignable quand l'élève travaille dans son outil, plein écran compris. */
  const openActivity = useCallback(() => {
    open('activity', '/activity');
  }, [open]);
  /** « Documents » — ce que l'enseignant a fait passer à la classe. Même geste que « Activité du
   * jour », et pour la même raison : une liste de documents accessible seulement depuis le bureau
   * nu disparaîtrait à la seconde où l'élève ouvre son outil pour travailler. */
  const openDocuments = useCallback(() => {
    open('documents', '/documents');
  }, [open]);

  /** Fermer et réduire font disparaître ce qu'on projetait : tous deux sortent du plein écran,
   * puis passent le premier plan à la fenêtre visible la plus haute — ou au bureau nu. */
  const applyAndRefocus = useCallback(
    (next: WindowStack) => {
      fs.exit();
      setStack(next);
      void navigate(nextFocusPath(next), { replace: true });
    },
    [fs, navigate],
  );

  const frameFor = (win: DeskWindow, index: number): WindowFrame => {
    const active = win.key === focusKey;
    return {
      active,
      storageKey: `window:${me.id}:${win.key}`,
      // Seule la fenêtre active peut être en plein écran : le plein écran appartient au document.
      fullscreen: fs.active && active,
      // Le bureau, lui, est en plein écran pour TOUT LE MONDE : ses marges disparaissent, donc la
      // place naturelle des fenêtres du fond bouge aussi. Voir `WindowFrame.deskFullscreen`.
      deskFullscreen: fs.active,
      minimized: win.minimized,
      // Les fenêtres restent sous le dock (50) et la barre de menu (60) de la référence.
      zIndex: Math.min(20 + index, 45),
      cascade: win.cascade,
      focus: () => {
        void navigate(win.path, { replace: true });
      },
      close: () => {
        applyAndRefocus(closeWindow(stack, win.key));
      },
      minimize: () => {
        applyAndRefocus(minimizeWindow(stack, win.key));
      },
      // Les DEUX commandes (pastille verte et bouton de la barre de menu) passent par ici : une
      // seule demande de plein écran, donc un seul comportement.
      toggleFullscreen: fs.toggle,
    };
  };

  const activeTitle = titleOf(windows.find((w) => w.key === focusKey));

  const deskContext: DeskContextValue = {
    me,
    // La classe AFFICHÉE : celle de l'élève dont on lit le travail, sinon celle du sélecteur. Sans
    // cela, un écran ouvert pendant l'observation (la consigne du jour, la relecture) parlerait de
    // la classe de l'enseignant pendant que la grille affiche celle de l'élève.
    classId: observing?.classId ?? classId,
    className,
    workspace: workspace.data,
    clock,
    openApp,
    openTeacher: openTeacherSection,
    observing,
    stopObserving: stopObservation,
  };

  // Ce que le bureau nu doit dire quand il n'a rien à montrer. Une phrase, jamais un bouton mort.
  const notice =
    deskApps.error instanceof Error
      ? deskApps.error.message
      : INSTALLED_APPS.length === 0
        ? t('dock.noApp')
        : null;

  return (
    <DeskProvider value={deskContext}>
      {classSelectionError && <p role="alert">{t('mission.workspace.storageError')}</p>}
      {me.sandboxAvailable === true && (
        <div className="desk-sandbox" role="status">
          {t('sandbox.banner')}
        </div>
      )}
      <MenuBar
        clock={clock}
        activeTitle={activeTitle}
        userName={me.displayName}
        classes={me.classes}
        selectedClassId={classId}
        onSelectClass={(id) => {
          if (!id || id === classId) return;
          try {
            switchClassSelection(me.id, id);
          } catch {
            setClassSelectionError(true);
          }
        }}
        onOpenProfile={openProfile}
        fullscreen={fs.active}
        onToggleFullscreen={fs.toggle}
      />

      {/* **Le bandeau qui ne quitte jamais l'écran.** Trente élèves, trente écrans qui se
          ressemblent : se tromper d'élève en plein débrief coûterait cher. Il est posé ICI, au
          niveau du bureau et non d'une fenêtre, et il reste visible en PLEIN ÉCRAN — c'est
          précisément le moment où la barre de menu et le dock s'escamotent, donc le moment où
          l'enseignant n'aurait plus rien d'autre pour se repérer. Le geste de sortie est à côté de
          la phrase, comme partout ailleurs dans ce bureau. */}
      {observing === null && selectedMission && (
        <div className="desk-observing" role="status">
          <span>{t('mission.workspace.banner', { title: selectedMission.title })}</span>
          {selectionError && <span role="alert">{t('mission.workspace.storageError')}</span>}
          {ownWorkspace.isError && <span role="alert">{t('mission.workspace.unavailable')}</span>}
          <button
            className="ndrc-button"
            type="button"
            onClick={() => {
              try {
                switchMissionSelection(me.id, null);
              } catch {
                setSelectionError(true);
              }
            }}
          >
            {t('mission.workspace.leave')}
          </button>
        </div>
      )}
      {observing === null ? null : (
        <div className="desk-observing" role="status">
          <span className="desk-observing-what">{t('observe.bannerLead')}</span>
          <strong className="desk-observing-who">{observing.displayName}</strong>
          <span className="desk-observing-mode">{t('observe.bannerReadOnly')}</span>
          <button type="button" className="ndrc-button" onClick={stopObservation}>
            {t('observe.leave')}
          </button>
        </div>
      )}

      <div className="desk" ref={deskRef} tabIndex={-1}>
        <div className="desk-actions">
          <ActionMenu
            label={t('desk.actions')}
            icon="settings"
            target={deskRef}
            actions={[
              {
                label: t('desk.arrange'),
                icon: 'grid',
                run: () => {
                  const visible = windows.filter((w) => !w.minimized);
                  const tiles = arrangeTiles(visible.length);
                  visible.forEach((w, index) =>
                    window.dispatchEvent(
                      new CustomEvent('ndrc:window-place', {
                        detail: {
                          key: `window:${me.id}:${w.key}`,
                          side: tiles[index],
                        },
                      }),
                    ),
                  );
                },
              },
            ]}
          />
        </div>
        {windows.map((win, index) => (
          <WindowSlot key={win.key} win={win} frame={frameFor(win, index)} />
        ))}
        {/* Le bureau nu n'est plus muet : il porte la consigne de la séance. C'est la première
            chose qu'un élève lit en s'asseyant, et c'était exactement ce qui manquait — « un élève
            qui se connecte voit un bureau avec des outils et aucune consigne ». Sous une fenêtre
            ouverte, la carte cède la place ; l'icône du dock la fait revenir à tout moment.

            La colonne enveloppe la carte ET l'avis : toutes les fenêtres du bureau partagent
            l'unique cellule `stack` de la grille (`desk.css`), donc deux enfants posés côte à côte
            s'y superposeraient — l'avis « aucune application installée » se serait imprimé PAR
            DESSUS la consigne, exactement dans le cas où l'un et l'autre ont le plus besoin d'être
            lus. */}
        {windows.length === 0 ? (
          <div className="desk-front">
            {notice === null ? null : <p className="desk-notice">{notice}</p>}
            <div className="activity-card">
              <ActivityBoard />
            </div>
          </div>
        ) : null}
      </div>

      <Dock
        apps={INSTALLED_APPS}
        enabledAppIds={enabledAppIds}
        activationState={
          visibleAppIds !== undefined
            ? 'ready'
            : (observing === null ? deskApps.isError : observedSpace.isError)
              ? 'unavailable'
              : 'loading'
        }
        runningAppIds={runningAppIds}
        onOpenApp={openApp}
        onOpenProfile={openProfile}
        profileRunning={stack.windows.some((w) => w.key === 'me')}
        showTeacher={me.role === 'teacher'}
        onOpenTeacher={openTeacher}
        teacherRunning={stack.windows.some((w) => w.key === 'teacher')}
        onOpenActivity={openActivity}
        activityRunning={stack.windows.some((w) => w.key === 'activity')}
        onOpenDocuments={openDocuments}
        documentsRunning={stack.windows.some((w) => w.key === 'documents')}
        onOpenPractice={() => open('practice', '/practice')}
        practiceRunning={stack.windows.some((w) => w.key === 'practice')}
        onOpenEnvironments={() => open('environments', '/environments')}
        environmentsRunning={stack.windows.some((w) => w.key === 'environments')}
      />

      {fs.active ? (
        <FullscreenHint>
          {t('hint.fullscreenBefore')}
          <kbd>{t('hint.fullscreenKey')}</kbd>
          {t('hint.fullscreenAfter')}
        </FullscreenHint>
      ) : null}
    </DeskProvider>
  );
}

/** Le nom que la barre de menu affiche pour la fenêtre du premier plan. Il vient du manifeste pour
 * une app — le cœur ne connaît aucun nom d'app — et de `fr.json` pour les trois écrans du cœur. */
function titleOf(win: DeskWindow | undefined): string {
  if (win === undefined) return t('menubar.desk');
  if (win.kind === 'me') return t('profile.title');
  if (win.kind === 'teacher') return t('teacher.title');
  if (win.kind === 'activity') return t('activity.title');
  if (win.kind === 'documents') return t('documents.title');
  if (win.kind === 'practice') return t('practice.title');
  if (win.kind === 'environments') return t('environments.title');
  return findManifest(win.appId ?? '')?.name ?? t('menubar.desk');
}
